import { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { datasetSliceName, spreadsheetSliceName, userDataSliceName } from '@/redux/constants';
import { setColumnDefs, setColumnState } from '@/redux/reducers/spreadsheetSlice';
import { showModal } from '@/redux/reducers/modalsSlice';
import {
  CHANGED_COLUMN_POSITION,
  DELETE_HIDDEN_COLUMNS,
  DELETE_SELECTED_COLUMNS,
  DELETE_VISIBLE_COLUMNS,
  MODAL_CREATE_ACCOUNT,
  MODAL_DELETE_COLUMN,
  MODAL_DELETE_COLUMNS,
  MODAL_MAKE_COPY,
  MODAL_RENAME_COLUMNS,
  MODAL_REQUIRE_ACCOUNT,
  TOAST_TEXT_GENERAL_SPREADSHEET_ERROR,
  TOAST_TYPE_ERROR,
  URL_DATASET,
} from '@/Utils/constants';
import { post } from '@/Utils/API';
import { getFileUuidFromPath } from '@/Utils/getFileUuidFromPath';

import ShowToast from '../Toast/showToastTemplate';
import CustomColumnManagerMobileVersion from './CustomColumnManagerMobileVersion';
import CustomColumnManagerDesktopVersion from './CustomColumnManagerDesktopVersion';
import { hasNoWritePermission } from '@/helpers/spreadsheetHelper';
import { useCurrentFile } from '@/hooks/useCurrentFile';
import { useShowPreviewModeUpsellModal } from '@/hooks/useShowPreviewModeUpsellModal';

const CustomColumnManager = ({ openDeleteColumnsPopup, agGrid, isMobileVersion }) => {
  const dispatch = useDispatch();
  const showPreviewModeUpsellModal = useShowPreviewModeUpsellModal();
  const {
    clientState: { columnState },
    columnDefs,
  } = useSelector((state) => state[spreadsheetSliceName]);
  const { user } = useSelector((state) => state[userDataSliceName]);
  const { isPreviewMode } = useSelector((state) => state[datasetSliceName]);
  const currentFile = useCurrentFile();

  const [columns, setColumns] = useState([]);
  const [query, setQuery] = useState('');
  const [filteredColumns, setFilteredColumns] = useState([]);
  const [numberOfVisibleColumns, setNumberOfVisibleColumns] = useState(0);
  const [isAllColumnsVisible, setIsAllColumnVisible] = useState(false);
  const [lastSelectedColumnCheckboxIndex, setLastSelectedColumnCheckboxIndex] = useState(null);
  const [lastSelectedColumnItemIndex, setLastSelectedColumnItemIndex] = useState(null);
  const [selectedColumn, setSelectedColumn] = useState([]);
  const [isContextMenuOpen, setIsContextMenuOpen] = useState(false);
  const [contextMenuTopPosition, setContextMenuTopPosition] = useState(false);

  const columnMenuRef = useRef(null);

  const checkIsPreviewMode = () => {
    if (hasNoWritePermission(currentFile)) return dispatch(showModal({ name: MODAL_MAKE_COPY }));
    if (user?.is_anonymous) return dispatch(showModal({ name: MODAL_REQUIRE_ACCOUNT }));
    if (isPreviewMode) {
      return showPreviewModeUpsellModal();
    }

    return false;
  };

  const handleColumnCheckbox = (event, col, index) => {
    const _columnState = agGrid.api.getColumnState();
    const currentSelectedColumnIndex = index;

    if (event.nativeEvent.shiftKey && lastSelectedColumnCheckboxIndex !== null) {
      const minColumnIndex = Math.min(lastSelectedColumnCheckboxIndex, currentSelectedColumnIndex);
      const maxColumnIndex = Math.max(lastSelectedColumnCheckboxIndex, currentSelectedColumnIndex);
      const properHideValue = !col.hide;

      const colIdList = [];
      for (let i = minColumnIndex; i <= maxColumnIndex; i++) {
        colIdList.push(filteredColumns[i].colId);
      }

      const updatedColumnState = _columnState.map((el) => {
        if (colIdList.includes(el.colId)) {
          return { ...el, hide: properHideValue };
        }
        return el;
      });

      dispatch(setColumnState(updatedColumnState));
    } else {
      const updatedColumnState = _columnState.map((el) => {
        if (col.colId === el.colId) {
          return { ...el, hide: !el.hide };
        }
        return el;
      });

      dispatch(setColumnState(updatedColumnState));
    }

    setLastSelectedColumnCheckboxIndex(currentSelectedColumnIndex);
  };

  const handleColumnSelection = (event, col, index) => {
    const currentSelectedColumnIndex = index;
    setIsContextMenuOpen(false);

    if (event.nativeEvent.shiftKey && lastSelectedColumnItemIndex !== null) {
      const minColumnIndex = Math.min(lastSelectedColumnItemIndex, currentSelectedColumnIndex);
      const maxColumnIndex = Math.max(lastSelectedColumnItemIndex, currentSelectedColumnIndex);

      const newSelectedList = [];

      for (let i = minColumnIndex; i <= maxColumnIndex; i++) {
        newSelectedList.push(filteredColumns[i]);
      }

      setSelectedColumn(newSelectedList);
    } else {
      if (event.nativeEvent.ctrlKey && lastSelectedColumnItemIndex !== null) {
        if (selectedColumn.some((el) => col.field === el.field && col.colId === el.colId)) {
          const updatedSelectedColumn = selectedColumn.filter((el) => el.colId !== col.colId);
          return setSelectedColumn(updatedSelectedColumn);
        }

        return setSelectedColumn([...selectedColumn, col]);
      }

      if (selectedColumn.some((el) => col.field === el.field && col.colId === el.colId)) {
        return setSelectedColumn([]);
      }

      setSelectedColumn([col]);
    }

    setLastSelectedColumnItemIndex(index);
  };

  const handleDeleteColumn = (col) => {
    if (checkIsPreviewMode()) return;

    if (col && col.colId) {
      dispatch(
        showModal({
          name: MODAL_DELETE_COLUMN,
          props: {
            columnId: col?.colId,
            columnName: col?.headerName,
          },
        })
      );
    }
  };

  const handleDeleteVisibleColumns = () => {
    if (user?.is_anonymous) {
      return dispatch(showModal({ name: MODAL_CREATE_ACCOUNT }));
    }
    if (checkIsPreviewMode()) return;

    openDeleteColumnsPopup(DELETE_VISIBLE_COLUMNS);
  };

  const handleDeleteHiddenColumns = () => {
    if (user?.is_anonymous) {
      return dispatch(showModal({ name: MODAL_CREATE_ACCOUNT }));
    }
    if (checkIsPreviewMode()) return;

    openDeleteColumnsPopup(DELETE_HIDDEN_COLUMNS);
  };

  const handleRenameColumns = () => {
    if (user?.is_anonymous) {
      return dispatch(showModal({ name: MODAL_CREATE_ACCOUNT }));
    }
    if (checkIsPreviewMode()) return;

    dispatch(
      showModal({
        name: MODAL_RENAME_COLUMNS,
      })
    );
  };

  const handleColumnNameQuery = (query) => {
    const filteredColumns =
      query === ''
        ? columns
        : columns.filter((col) => {
            return col.headerName.toLowerCase().includes(query.toLowerCase());
          });
    setFilteredColumns(filteredColumns);
  };

  const toggleColumnVisibility = (value = null) => {
    const _columnState = agGrid.api.getColumnState();
    const updatedColumnState = _columnState.map((el) => {
      return { ...el, hide: value === null ? !!numberOfVisibleColumns : value };
    });
    dispatch(setColumnState(updatedColumnState));
  };

  const handleDragStart = (e, col) => {
    if (checkIsPreviewMode()) return;

    const _index = columns.findIndex((el) => el.field === col.field);
    e.dataTransfer.setData('index', _index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (e, col) => {
    if (checkIsPreviewMode()) return;

    const sourceIndex = e.dataTransfer.getData('index');
    const _targetIndex = columnDefs.findIndex(
      (el) => el.field === col.field && el.colId === el.colId
    );
    if (+sourceIndex <= 0 || +_targetIndex <= 0 || +_targetIndex === +sourceIndex) return;
    const updatedColumnDefs = [...columnDefs];
    const [movedItem] = updatedColumnDefs.splice(sourceIndex, 1);

    const properTargetIndex =
      +sourceIndex > _targetIndex
        ? _targetIndex
        : _targetIndex - +sourceIndex > 1
        ? _targetIndex - 1
        : _targetIndex;

    updatedColumnDefs.splice(properTargetIndex, 0, movedItem);

    const uuid = getFileUuidFromPath();
    const data = {
      col_id: col.colId,
      from_index: +sourceIndex,
      to_index: properTargetIndex,
    };

    try {
      dispatch(setColumnDefs(updatedColumnDefs));
      const endpoint = `${URL_DATASET}/${uuid}/${CHANGED_COLUMN_POSITION}`;
      const payload = data;
      const response = await post(endpoint, payload);
      if (!response?.Success) {
        ShowToast({
          type: TOAST_TYPE_ERROR,
          text: TOAST_TEXT_GENERAL_SPREADSHEET_ERROR,
          errorContext: response,
          endpoint: endpoint,
          payload: payload,
          fileAndFunction: '__FILE_AND_FUNCTION_NAME__',
        });
      }
    } catch (error) {
      throw new Error(error);
    }
  };

  const handleRightMouseClick = (e, col) => {
    if (checkIsPreviewMode()) return;

    if (selectedColumn.some((el) => col.field === el.field && col.colId === el.colId)) {
      e.preventDefault();
      const { top } = e.target.getBoundingClientRect();
      const { top: columnMenuTopPosition } = columnMenuRef.current.getBoundingClientRect();
      setContextMenuTopPosition(Math.floor(top - columnMenuTopPosition));
      return setIsContextMenuOpen(true);
    }
    setIsContextMenuOpen(false);
  };

  const handleDeleteSelectedColumns = async () => {
    if (checkIsPreviewMode()) return;

    if (selectedColumn.length) {
      dispatch(
        showModal({
          name: MODAL_DELETE_COLUMNS,
          props: { columnsToDelete: DELETE_SELECTED_COLUMNS, columnsToDeleteList: selectedColumn },
        })
      );
    }
  };

  const handleSearchValue = (event) => {
    setQuery(event.target.value);
    isContextMenuOpen && setIsContextMenuOpen(false);
    selectedColumn.length && setSelectedColumn([]);
  };

  useEffect(() => {
    const _columnState = agGrid.api.getColumnState();

    let numberOfVisibleColumns = 0;
    const numberOfColumns = columnDefs.length;
    const unitedColumnDefsAndColumnState = columnDefs.map((colDef) => {
      return { ...colDef, ..._columnState.find((el) => el.colId === colDef.colId) };
    });

    _columnState.forEach(({ hide }) => {
      !hide && numberOfVisibleColumns++;
    });

    setNumberOfVisibleColumns(numberOfVisibleColumns);
    setIsAllColumnVisible(numberOfColumns === numberOfVisibleColumns);
    setColumns(unitedColumnDefsAndColumnState);
    setIsContextMenuOpen(false);
  }, [columnState, columnDefs]);

  useEffect(() => {
    handleColumnNameQuery(query);
  }, [columns, query]);

  return isMobileVersion ? (
    <CustomColumnManagerMobileVersion
      filteredColumns={filteredColumns}
      handleColumnCheckbox={handleColumnCheckbox}
      isMobileVersion={isMobileVersion}
      toggleColumnVisibility={toggleColumnVisibility}
      numberOfVisibleColumns={numberOfVisibleColumns}
      isAllColumnsVisible={isAllColumnsVisible}
    />
  ) : (
    <div ref={columnMenuRef}>
      <CustomColumnManagerDesktopVersion
        agGrid={agGrid}
        filteredColumns={filteredColumns}
        handleColumnCheckbox={handleColumnCheckbox}
        toggleColumnVisibility={toggleColumnVisibility}
        numberOfVisibleColumns={numberOfVisibleColumns}
        isAllColumnsVisible={isAllColumnsVisible}
        isContextMenuOpen={isContextMenuOpen}
        contextMenuTopPosition={contextMenuTopPosition}
        handleDeleteSelectedColumns={handleDeleteSelectedColumns}
        query={query}
        handleSearchValue={handleSearchValue}
        handleDragStart={handleDragStart}
        handleDragOver={handleDragOver}
        handleDrop={handleDrop}
        handleColumnSelection={handleColumnSelection}
        handleDeleteColumn={handleDeleteColumn}
        selectedColumn={selectedColumn}
        handleRightMouseClick={handleRightMouseClick}
        handleRenameColumns={handleRenameColumns}
        handleDeleteVisibleColumns={handleDeleteVisibleColumns}
        handleDeleteHiddenColumns={handleDeleteHiddenColumns}
      />
    </div>
  );
};

export default CustomColumnManager;
